import { Dispatch, SetStateAction } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ISaveProgram } from "../../interfaces/services";
import { EtyProgram } from "../../interfaces/entities";
import { IProgramForTree, ISelectedData } from "../../interfaces/tree";

import useToast from "../useToast";

import { saveProgram } from "../../services/programs";

export interface IPayload {
    payload: ISaveProgram;
    setIsProcessing: Dispatch<SetStateAction<boolean>>;
    selected: ISelectedData | null;
    setSelected: Dispatch<SetStateAction<ISelectedData | null>>;
};

export interface IResponse {
    error?: string;
    message: string;
    statusCode: number;
    data: EtyProgram;
};

const useSaveProgram = () => {
    const queryClient = useQueryClient();

    const { addToast } = useToast();

    const mutationFn = async (data: IPayload): Promise<IResponse> => {
        return await saveProgram(data.payload);
    };

    return useMutation({
        mutationFn,
        onSuccess: (response: IResponse, variables: IPayload) => {
            if (response?.statusCode === 200) {
                addToast({
                    type: 'success',
                    title: 'Program saved successfully.',
                });

                if (variables.selected) {
                    variables.setSelected({
                        id: response.data.id,
                        level: 0,
                        data: {
                            ...(variables.selected.data as IProgramForTree),
                            id: response.data.id,
                            active: response.data.active,
                            name: response.data.name,
                            subtitle: response.data.subtitle,
                            description: response.data.description,
                            gov_url: response.data.gov_url,
                            method: response.data.method,
                            difficulty: response.data.difficulty,
                            cost: response.data.cost,
                            timeframe: response.data.timeframe,
                            date_modified: response.data.date_modified,
                        }
                    });
                };

                queryClient.invalidateQueries({ queryKey: ['programs_tree'] });
            };
        },
        onError: (error: IResponse, variables: IPayload) => {
            addToast({
                type: 'error',
                title: 'Error occured',
                message: error.message
            });
        },
        onSettled: (data: IResponse | undefined, error: IResponse | null, variables: IPayload) => {
            variables.setIsProcessing(false);
        },
    });
};

export default useSaveProgram;